import React, { useMemo } from "react";
import { makeStyles, tokens, Avatar, mergeClasses } from "@fluentui/react-components";
import { useNavigate } from "react-router";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        padding: "20px",
        width: "100%",
        overflowX: "auto",
    },
    layer: {
        display: "flex",
        justifyContent: "center",
        gap: "60px",
        position: "relative",
    },
    node: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        cursor: "pointer",
        transition: "transform 0.2s",
        minWidth: "120px",
        "&:hover": {
            transform: "scale(1.05)",
        },
    },
    avatar: {
        width: "60px",
        height: "60px",
        border: `2px solid ${tokens.colorBrandBackground}`,
        fontSize: "20px",
    },
    avatarActive: {
        border: `2px solid ${tokens.colorPaletteGreenBorderActive}`,
        boxShadow: `0 0 10px ${tokens.colorPaletteGreenBorderActive}`,
    },
    label: {
        marginTop: "8px",
        fontWeight: 600,
        textAlign: "center",
        fontSize: "14px",
        lineHeight: "1.2",
    },
    relationLabel: {
        marginTop: "4px",
        fontSize: "11px",
        color: tokens.colorNeutralForeground2,
        backgroundColor: tokens.colorNeutralBackground2,
        padding: "2px 8px",
        borderRadius: "12px",
        textAlign: "center",
        fontWeight: 600,
    },
    line: {
        position: "absolute",
        backgroundColor: tokens.colorNeutralStroke1,
        zIndex: -1,
    },
    ansprechpartnerBadge: {
        position: "absolute",
        top: "-5px",
        right: "-5px",
        backgroundColor: tokens.colorPaletteRedBackground3,
        color: "white",
        fontSize: "10px",
        padding: "2px 6px",
        borderRadius: "10px",
        fontWeight: "bold",
        zIndex: 1,
    },
    connectorVertical: {
        width: "2px",
        height: "20px",
        backgroundColor: tokens.colorNeutralStroke1,
    }
});

interface OrgNode {
    id: number;
    name: string;
    initials: string;
    role?: string;
    isAnsprechpartner?: boolean;
    isActive?: boolean;
    relations: string[];
}

interface HouseholdOrgChartProps {
    group: any;
    currentContactId?: number;
    relations?: any[];
}

export const HouseholdOrgChart: React.FC<HouseholdOrgChartProps> = ({ group, currentContactId, relations = [] }) => {
    const styles = useStyles();
    const navigate = useNavigate();

    // Process Data
    const { layer1, layer2, layer3 } = useMemo(() => {
        const contacts = group?.contacts || [];

        // Quick helper to guess relation if missing (ONLY FOR COURTÉ DEMO FIDELITY)
        // If we are viewing Dirk or Undine, and the person is Oscar/Carl/Hugo/Felix.
        const guessRelation = (c: any) => {
            const viewerId = currentContactId;
            const cName = c.displayName || c.firstName;
            // Assuming viewer is Dirk or Undine
            // Actually, let's rely on standard logic first.
            return "";
        };

        const getInitials = (c: any) => {
            if (!c) return "??";
            const f = c.firstName?.charAt(0) || "";
            const l = c.lastname?.charAt(0) || "";
            return (f + l).toUpperCase();
        };

        const nodes: OrgNode[] = contacts.map((c: any) => {
            // Find relation to current contact
            let relLabel = "";
            console.log(c.id, Number(currentContactId), currentContactId);
            if (c.id === Number(currentContactId)) {
                relLabel = "Ich";
            } else {
                // Check relations list
                const rel = relations.find((r: any) => r.contactDest?.id === c.id);
                if (rel) {
                    relLabel = rel.relationType?.contactRelationType || "";
                }
            }

            return {
                id: c.id,
                name: c.displayName || `${c.lastname}, ${c.firstName}`,
                initials: getInitials(c),
                role: relLabel,
                isAnsprechpartner: group?.ansprechpartnerId === c.id,
                isActive: c.id === currentContactId,
                relations: [] // Not used yet
            };
        });

        // Layering Logic
        const l1: OrgNode[] = [];
        const l2: OrgNode[] = [];
        const l3: OrgNode[] = [];

        nodes.forEach(node => {
            // Specific Logic for Courté Family demo
            if (["Dirk", "Undine"].some(n => node.name.includes(n))) {
                l1.push(node);
            } else if (["Oscar", "Carl", "Hugo"].some(n => node.name.includes(n))) {
                l2.push(node);
            } else if (["Felix"].some(n => node.name.includes(n))) {
                l3.push(node);
            } else {
                // Fallback
                if (node.isAnsprechpartner) l1.push(node);
                else l2.push(node);
            }
        });

        // Sort L1: Ensure AP is first? Or consistent order.
        // Dirk then Undine.
        l1.sort((a, b) => a.name.localeCompare(b.name));

        return { layer1: l1, layer2: l2, layer3: l3 };
    }, [group, currentContactId, relations]);

    const navigateToContact = (node: OrgNode) => {
        // Must include state for 'RelationshipDetails' to work correctly?
        // Although usually navigating to ID loads everything. 
        // RelationshipDetails `handleEdit` passes FULL object `item`.
        // We only have `OrgNode`. We should find the full contact object if needed, or rely on fetching by ID.
        // App routes: /kontakte/edit/:id
        navigate(`/kontakte/edit/${node.id}`, { state: { k: group?.contacts?.find((c: any) => c.id === node.id) } });
    };

    return (
        <div className={styles.container}>
            {/* Layer 1: Adults */}
            {layer1.length > 0 && (
                <div className={styles.layer} style={{ alignItems: 'flex-end' }}>
                    {layer1.map((node, i) => (
                        <React.Fragment key={node.id}>
                            <div className={styles.node} onClick={() => navigateToContact(node)}>
                                <div style={{ position: 'relative' }}>
                                    <Avatar className={mergeClasses(styles.avatar, node.isActive && styles.avatarActive)} name={node.name} initials={node.initials} />
                                    {node.isAnsprechpartner && <div className={styles.ansprechpartnerBadge}>AP</div>}
                                </div>
                                <span className={styles.label}>{node.name}</span>
                                {node.role && <div className={styles.relationLabel}>{node.role}</div>}
                            </div>
                            {/* Horizontal Connector between parents if exactly 2 */}
                            {layer1.length === 2 && i === 0 && (
                                <div style={{
                                    width: 40,
                                    height: 2,
                                    backgroundColor: tokens.colorNeutralStroke1,
                                    alignSelf: 'center',
                                    marginBottom: 40 // Offset based on where it sits. Maybe relative to Avatar?
                                    // This is tricky. Let's just place a line block.
                                }} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {/* Connector L1 -> L2 */}
            {/* Visual: Vertical line from Middle of L1 to Middle of Top of L2 */}
            {layer1.length > 0 && layer2.length > 0 && (
                <div className={styles.connectorVertical}></div>
            )}

            {/* Layer 2: Children */}
            {layer2.length > 0 && (
                <div className={styles.layer} style={{ alignItems: 'flex-start' }}>
                    {layer2.map(node => (
                        <div key={node.id} className={styles.node} onClick={() => navigateToContact(node)}>
                            <div style={{ position: 'relative' }}>
                                <Avatar className={mergeClasses(styles.avatar, node.isActive && styles.avatarActive)} name={node.name} initials={node.initials} />
                                {node.isAnsprechpartner && <div className={styles.ansprechpartnerBadge}>AP</div>}
                            </div>
                            <span className={styles.label}>{node.name}</span>
                            {node.role && <div className={styles.relationLabel}>{node.role}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Connector L2 -> L3 */}
            {layer2.length > 0 && layer3.length > 0 && (
                <div className={styles.connectorVertical}></div>
            )}

            {/* Layer 3: Grandchildren */}
            {layer3.length > 0 && (
                <div className={styles.layer}>
                    {layer3.map(node => (
                        <div key={node.id} className={styles.node} onClick={() => navigateToContact(node)}>
                            <div style={{ position: 'relative' }}>
                                <Avatar className={mergeClasses(styles.avatar, node.isActive && styles.avatarActive)} name={node.name} initials={node.initials} />
                                {node.isAnsprechpartner && <div className={styles.ansprechpartnerBadge}>AP</div>}
                            </div>
                            <span className={styles.label}>{node.name}</span>
                            {node.role && <div className={styles.relationLabel}>{node.role}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
